import React, { useEffect } from 'react';
import { Switch, Checkbox } from '@adobe/react-spectrum';
import { FormField } from '../../store/slice';
import { updateFieldVisibility } from '../utils/fieldVisibility';
import styles from '../FormField.module.scss';

interface SwitchComponentProps {
  field: FormField;
  value: any;
  onChange: (name: string, value: any) => void;
  disabled?: boolean;
}

/**
 * Component for rendering switch and checkbox fields
 */
export const SwitchComponent: React.FC<SwitchComponentProps> = ({
  field,
  value,
  onChange,
  disabled = false
}) => {
  // Initialize checkbox/switch to false if value is undefined
  useEffect(() => {
    if (
      (field.type === 'checkbox' || field.type === 'switch') &&
      (value === undefined || value === null)
    ) {
      onChange(field.name, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  if (field.type === 'checkbox') {
    // If checkbox has options, use traditional checkbox style matching Features component
    if (field.options && field.options.length > 0) {
      const option = field.options[0];
      // Ensure boolean value (default to false if undefined)
      const isSelected = value === true;

      return (
        <div className={styles.checkboxContainer}>
          <label className={styles.fieldLabel}>
            {field.label}
            {field.required && <span className={styles.required}> *</span>}
          </label>
          <label className={styles.checkboxLabelWrapper}>
            <input
              type="checkbox"
              checked={isSelected}
              onChange={e => {
                // Always send explicit boolean (true/false)
                onChange(field.name, e.target.checked);
              }}
              data-testid={`checkbox-${field.name
                .replace(/[^a-zA-Z0-9]/g, '-')
                .toLowerCase()}`}
            />
            <span className={styles.checkboxVisual} />
            <span className={styles.checkboxText}>{option.label}</span>
          </label>
        </div>
      );
    }

    // Legacy checkbox without options - use Spectrum Checkbox
    return (
      <Checkbox
        UNSAFE_className={styles.checkbox}
        key={field.id}
        name={field.name}
        isSelected={value === true} // Explicit boolean check
        onChange={val => onChange(field.name, val)} // val is already boolean from Spectrum
        data-testid={`checkbox-${field.name
          .replace(/[^a-zA-Z0-9]/g, '-')
          .toLowerCase()}`}
      >
        {field.label}
        {field.required && <span className={styles.required}> *</span>}
      </Checkbox>
    );
  }

  return (
    <Switch
      UNSAFE_className={styles.switch}
      key={field.id}
      isSelected={value === true} // Explicit boolean check (defaults to false)
      isDisabled={disabled}
      onChange={val => {
        // Only allow changes if not disabled
        if (disabled) return;

        onChange(field.name, val); // val is already boolean from Spectrum

        // Check if this field has dependents and actions
        if (field.dependents && field.actions && field.dependents.length > 0) {
          // Apply actions to dependent fields based on switch state
          field.dependents.forEach(dependentField => {
            // If switch is on (true) - use first action (typically 'show')
            // If switch is off (false) - use second action (typically 'hide')
            const actionIndex = val ? 0 : 1;
            const action = field.actions?.[actionIndex];

            // Apply the action using our visibility state manager
            if (action === 'show') {
              // Show the dependent field
              updateFieldVisibility(dependentField, true);
            } else if (action === 'hide') {
              // Hide the dependent field
              updateFieldVisibility(dependentField, false);
            }
          });
        }
      }}
      data-testid={`switch-${field.name
        .replace(/[^a-zA-Z0-9]/g, '-')
        .toLowerCase()}`}
    >
      <span
        className={`${styles.switchLabel} ${value ? styles.active : ''} ${
          !disabled ? styles.enabled : ''
        }`}
      >
        {field.label}
        {field.required && <span className={styles.required}> *</span>}
      </span>
    </Switch>
  );
};
